import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { AnalysisTable } from '../api/types';
import type { TableSqlEntry } from '../types';
import { getTableSqlEntries } from '../utils/tableSqlEntries';

export interface UseTableSqlDrawerResult {
  entries: TableSqlEntry[];
  /** False when the definition carries no SQL for this table: nothing to open. */
  hasSql: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  openLabel: string;
  closeLabel: string;
  heading: string;
  columnsHeading: string;
  rowsHeading: string;
  copyLabel: string;
  copiedLabel: string;
}

// Owns the open state and the SQL entries behind a table's "View table row SQL" drawer.
export const useTableSqlDrawer = (
  definitionTable: AnalysisTable | null | undefined
): UseTableSqlDrawerResult => {
  const { formatMessage } = useIntl();
  const entries = useMemo(() => getTableSqlEntries(definitionTable), [definitionTable]);
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = useCallback(() => setIsOpen(true), []);
  const onClose = useCallback(() => setIsOpen(false), []);

  return {
    entries,
    hasSql: entries.length > 0,
    isOpen,
    onOpen,
    onClose,
    openLabel: formatMessage({ id: 'analysisDetail.tables.sql.open' }),
    closeLabel: formatMessage({ id: 'analysisDetail.tables.sql.close' }),
    heading: formatMessage({ id: 'analysisDetail.tables.sql.heading' }),
    columnsHeading: formatMessage({ id: 'analysisDetail.tables.sql.columns' }),
    rowsHeading: formatMessage({ id: 'analysisDetail.tables.sql.rows' }),
    copyLabel: formatMessage({ id: 'analysisDetail.tables.sql.copy' }),
    copiedLabel: formatMessage({ id: 'analysisDetail.tables.sql.copied' }),
  };
};
